import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';

export default function AppSignup({ onDone, onBackToLogin }: { onDone: () => void; onBackToLogin: () => void }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Pre-fill referral code if one was captured (e.g. from a ?ref= link the user opened earlier).
  useEffect(() => {
    const saved = localStorage.getItem('altcoins.refCode');
    if (saved) setReferralCode(saved.toUpperCase());
  }, []);

  async function submit() {
    if (busy) return;
    setError(null);
    if (!email.trim() || password.length < 8) { setError('Enter a valid email and a password of at least 8 characters.'); return; }
    setBusy(true);
    try {
      await authApi.signup({
        email: email.trim(),
        password,
        ...(referralCode.trim() ? { referralCode: referralCode.trim().toUpperCase() } : {}),
      });
      // Backend auto-verifies email, so log straight in.
      await login(email.trim(), password);
      localStorage.removeItem('altcoins.refCode'); // consumed
      onDone();
    } catch (e: any) {
      setError(e?.message || 'Could not create your account. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = { backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.08)', color: 'white' } as const;

  return (
    <div className="fixed inset-0 flex flex-col justify-center px-6 text-white overflow-y-auto"
      style={{
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        backgroundColor: '#0A0918',
        backgroundImage:
          'radial-gradient(at 18% 14%, rgba(139,92,246,0.25) 0, transparent 48%),' +
          'radial-gradient(at 82% 16%, rgba(236,72,153,0.20) 0, transparent 48%)',
      }}>
      <div className="py-8">
        <div className="text-2xl font-bold mb-1 text-center" style={{ fontFamily: '"Space Grotesk", sans-serif', background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          AltCoinsMoney
        </div>
        <h1 className="text-3xl font-bold text-center" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Create account</h1>
        <p className="text-center text-sm mb-7" style={{ color: '#8B82A8' }}>Join and start earning</p>

        <label className="text-sm mb-1 block" style={{ color: '#c4b5fd' }}>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" inputMode="email"
          className="w-full rounded-2xl px-4 py-3 mb-4 outline-none" style={inputStyle} />

        <label className="text-sm mb-1 block" style={{ color: '#c4b5fd' }}>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-2xl px-4 py-3 mb-1 outline-none" style={inputStyle} />
        <p className="text-[11px] mb-4" style={{ color: '#8B82A8' }}>At least 8 characters.</p>

        <label className="text-sm mb-1 block" style={{ color: '#c4b5fd' }}>Referral code <span style={{ color: '#52526E' }}>(optional)</span></label>
        <input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off"
          placeholder="e.g. 4ZVR5TY" className="w-full rounded-2xl px-4 py-3 mb-4 outline-none" style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace' }} />

        {error && <div className="rounded-2xl px-4 py-3 mb-4 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{error}</div>}

        <button onClick={submit} disabled={busy} className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Creating account…' : 'Create account'}
        </button>

        <button onClick={onBackToLogin} className="w-full mt-3 text-sm" style={{ color: '#8B82A8' }}>
          Already have an account? <span style={{ color: '#c4b5fd' }}>Sign in</span>
        </button>
      </div>
    </div>
  );
}